/**
 * httpSigning — HMAC signature computation for SL internal-actor auth.
 *
 * Extracted from serviceLayerClient.ts 2026-04-24 for guard-file-size
 * compliance. Pure functions; HMAC secret is read at call-site from
 * import.meta.env so this module has no env dependency.
 *
 * Signing input format: `${bodyB64}.${timestamp}.${nonce}` (HMAC-SHA256).
 * Consumers attach the returned headers when the HMAC secret is set
 * (dev/test internal-actor flows only — production uses cookie auth).
 */

export interface HmacHeaders {
  'X-Internal-Actor': string;
  'X-Timestamp': string;
  'X-Nonce': string;
  'X-Signature': string;
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlEncodeString(value: string): string {
  return base64UrlEncode(new TextEncoder().encode(value).buffer);
}

export async function computeHmacSignature(
  secret: string,
  body: string,
  timestamp: string,
  nonce: string,
): Promise<string | null> {
  if (!secret) return null;
  if (typeof crypto === 'undefined' || !crypto.subtle) return null;
  const encoder = new TextEncoder();
  const bodyB64 = body ? base64UrlEncodeString(body) : '';
  const signingInput = `${bodyB64}.${timestamp}.${nonce}`;
  const keyData = encoder.encode(secret);
  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(signingInput));
  return base64UrlEncode(signature);
}

/**
 * Build a fresh (timestamp, nonce) pair and compute the HMAC signature.
 * Returns the full header set ready to spread onto request headers, or
 * null if signing is not possible (no secret / no crypto.subtle).
 */
export async function buildHmacHeaders(
  secret: string | undefined,
  actor: string | undefined,
  body: string,
): Promise<HmacHeaders | null> {
  if (!secret) return null;
  const timestamp = Date.now().toString();
  const nonce =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `nonce_${Math.random().toString(36).slice(2)}`;
  const signature = await computeHmacSignature(secret, body, timestamp, nonce);
  if (!signature) return null;
  return {
    'X-Internal-Actor': actor || 'clanda-command',
    'X-Timestamp': timestamp,
    'X-Nonce': nonce,
    'X-Signature': signature,
  };
}
